<template>
    <div class="checkin-by-phone">
        <div v-if="action === 'search_phone'">
            <!-- Step 1b: Search by name if multiple bookings were found -->
            <p>{{ $t('checkin_by_phone.instruction') }}</p>
            <form @submit.prevent="submitByPhone">
                <div class="form-group">
                    <label class="phone-info">
                        {{ $t('checkin_by_phone.label_checkin_date') }}
                    </label>
                    <input class="form-control text-center" type="text" :value="checkin_date" readonly />
                </div>
                <div class="form-group">
                    <label class="phone-info">
                        {{ $t('checkin_by_phone.label_phone_number') }}
                        <i class="fas fa-info-circle text-primary" v-b-tooltip.hover
                            :title="$t('pre_checkin_bmatch.wo_phone_tips')" />
                    </label>
                    <input
                    type="text"
                    class="mt-2 form-control"
                    v-model="phone_raw"
                    required
                    :placeholder="$t('pre_checkin_bmatch.wo_phone_placeholder')"
                    @keypress="onlyNumbersAndPlus"
                    />
                </div>
                <div class="text-right">
                    <button type="submit" class="btn btn-form-control btn-custom-primary" :disabled="!phone_raw">
                        {{ $t("visit-information-form.confirm") }}
                    </button>
                </div>
                <div v-if="info_msg" class="alert alert-info mt-3 mb-0">
                    {{ info_msg }}
                </div>
                <div v-if="error_msg" class="alert alert-danger mt-3 mb-0">
                    {{ error_msg }}
                </div>
            </form>
        </div>

        <!-- Step 1b: Search by name if multiple bookings were found show if multiple reservations found in check by phone -->
        <div v-if="action === 'show_list_names'">
            <p>{{ $t('checkin_by_phone.instruction_list_of_guest_name') }}</p>
            <div>
               <p>{{ $t('checkin_by_phone.name_text') }} :</p>
            </div>
            <ul class="list-group guest-list-group">
                <li v-for="(booking, index) in masked_guest_names" :key="index"
                    @click="selectGuest(booking.original)"
                    class="guest-list-item"
                    @mouseover="hoveredIndex = index" @mouseleave="hoveredIndex = null"
                    :class="{ 'active': hoveredIndex === index }">
                    <input type="text" class="form-control guest-list-input" style="cursor: pointer;" :value="booking.masked" readonly />
                </li>
            </ul>
        </div>
        <div v-if="action === 'search_name'">
            <p>{{ $t('checkin_by_phone.instruction_guest_name') }}</p>
            <form @submit.prevent="submitByGuestName">
                <div class="form-group search-suggestion-box">
                    <label class="phone-info">
                        {{ $t('checkin_by_phone.label_guest_name') }} :
                    </label>
                    <input class="form-control search_field" type="text" v-model="guest_name" />
                </div>
                <div class="text-right">
                    <button type="submit" class="btn btn-form-control btn-custom-primary" :disabled="!guest_name">
                        {{ $t("visit-information-form.confirm") }}
                    </button>
                </div>
                <div v-if="info_msg" class="alert alert-info mt-3 mb-0">
                    {{ info_msg }}
                </div>
                <div v-if="error_msg" class="alert alert-danger mt-3 mb-0">
                    {{ error_msg }}
                </div>
            </form>
        </div>
    </div>
</template>

<script>
import moment from 'moment-timezone';
import { booking_status } from '../../../utils/mockup';
import _ from "lodash";
import { onlyNumbersAndPlus } from "../../../utils/helpers";

// Stop checkout conditions
// const checkedOutStatusIds = [3, 10, 11, 15, 16, 17];

const checkedOutStatusIds = [
    booking_status.checked_out,
    booking_status.ready_to_pay_checkout,
    booking_status.checkout_completed,
    booking_status.check_out_hold,
    booking_status.check_out_payment,
    booking_status.check_out_completed_by_system,
];

export default {
    name: 'CheckinByPhone',
    props: {
        facility: Object,
        confirm: Boolean,
        loadCustomTextSetting: Function,
        localized_filters: Array,
        selectFilter: Function,
        selected_localized_category: Object,
        loadQuestions: Function,
        booking_details: {
            type: Object,
            required: true
        },
        action: {
            type: String,
            required: true
        },
        cameFromPciHub: {
            type: Boolean,
            default: false
        },
        // Reservation check-in date to search by (facility timezone); the previous day during the window time extension
        checkinDate: {
            type: String,
            default: ''
        }
    },
    data() {
        return {
            phone_raw: '',
            phone_number: '',
            info_msg: '',
            error_msg: '',
            guest_name: '',
            booking_details_fetched: [],
            selected_guest_booking: null,
            hoveredIndex: null,
        }
    },
    computed: {
        checkin_date() {
            return this.checkinDate || moment().format('YYYY/MM/DD');
        },
        masked_guest_names() {
            if (!this.booking_details_fetched || !this.booking_details_fetched.data) {
                return [];
            }
            // Masking rule: 2 letters: *a, 3: *aa, 4: **aa, 5: **aaa, 6: ***aaa, 7: ****aaa, 8: *****aaa
            return this.booking_details_fetched.data.map(booking => {
                const name = booking.parsed.full_name;
                const masked = this.maskName(name);
                return {
                    masked,
                    original: booking
                };
            });
        }
    },
   methods: {
      onlyNumbersAndPlus,
      selectGuest(booking) {
         this.selected_guest_booking = booking;
         this.guest_name = '' ; // reset guest name input
         this.error_msg = '' ;
         this.$emit('update-action', 'search_name');
      },
      phoneCountryChange(v) {
         this.phone_country_code = v.dialCode;
      },
      phoneInput(v, o) {
         // Get phone number only (excluding country code)
         this.phone_number = o.number.significant;
      },
      maskName(name) {
         const len = name.length;
         
         // The original logic is implemented here as a single set of rules.
         // Determine how many characters to keep visible at the end.
         let visibleCount = 0;
         if (len > 8) {
            visibleCount = 3; // Equivalent to: '*'.repeat(len - 3) + name.slice(-3);
         } else if (len === 8) {
            visibleCount = 3; // ***** + slice(-3)
         } else if (len === 7) {
            visibleCount = 3; // **** + slice(-3)
         } else if (len === 6) {
            visibleCount = 3; // *** + slice(-3)
         } else if (len === 5) {
            visibleCount = 3; // ** + slice(-3)
         } else if (len === 4) {
            visibleCount = 2; // ** + slice(-2)
         } else if (len === 3) {
            visibleCount = 2; // * + slice(-2)
         } else if (len === 2) {
            visibleCount = 1; // * + slice(-1)
         } 
         // For len <= 1, visibleCount remains 0, masking is skipped.

         // If no characters are visible, or the name is too short, return the original name.
         if (visibleCount === 0 || len <= 1) {
            return name;
         }
         
         // Calculate how many characters need to be masked
         const maskedLength = len - visibleCount;
         
         // If the name is too short and would result in 0 or negative mask length, 
         // just return the name (already covered by len <= 1, but good for safety).
         if (maskedLength <= 0) {
            return name;
         }

         const maskedPart = '*'.repeat(maskedLength);
         const visiblePart = name.slice(-visibleCount);
         
         return maskedPart + visiblePart;
      },
      async submitByPhone(e) {

         this.resetState();

         const payload = {
               'facility_id': this.facility.id,
               'mode': 'phone',
               'search': this.phone_raw,
               'checkin_date': moment(this.checkin_date).format('YYYY-MM-DD')
         };

         const response = await this.$lionheart.post('/facility-booking/list', payload)
               .then((r) => r.data)
               .catch((e) => this.catcher(e));

         // Reset questionnaire form to input
         this.confirm = false;

         const result = response?.data?.[0];
         const total = response?.total ?? 0;

         if (total < 1) {
               // stop no reservation found
               this.error_msg = this.$t('checkin_by_phone.error_no_reservation');
               return;
         }

         if (total > 1) {
               // multiple reservation 
               this.$emit('update-action', 'show_list_names');
               this.booking_details_fetched = response;
               return;
         }

         const status = result.booking_status_id;

         if (status === booking_status.checked_in) {
               this.info_msg = this.$t('checkin_by_phone.info_already_checkedin');
               return;
         }

         if (checkedOutStatusIds.includes(status)) {
               this.error_msg = this.$t('checkin_by_phone.error_already_checkedout');
               return;
         }

         // this.booking_details = response; causes mutation prop error
         const matchBooking = _.cloneDeep(response); // deep clone

         if (status === booking_status.pre_check_in && this.cameFromPciHub) {
               // Guest already completed pre-checkin (by phone or by name) - don't let the
               // PCI hub flow re-enter the confirm/finish-precheckin screen and re-trigger it.
               this.info_msg = this.$t('checkin_by_phone.info_pci_already_completed');
               return;
         }

         this.$emit('update-booking', matchBooking); // update the parent boooking details

         if (status === booking_status.pre_check_in) {
               this.$emit('update-booking', matchBooking);
               this.$emit('update-next', 'checkin-form')
         } else {

               if (this.localized_filters.length > 0) {
                  this.$emit('update-booking', matchBooking);
                  this.$emit('update-next', 'localized-filters'); // signal the parent component to show the localized filter in view
                  this.$emit('update-action', '');
               } else {
                  // upodate booking details and next in the parent form to proceed
                  this.$emit('update-booking', matchBooking);
                  this.$emit('update-next', 'checkin-form')
                  this.$emit('update-action', '');
               }
         }
      },
      async submitByGuestName(e) {
         const guestName = this.guest_name;
         const normalizeSearchableNameString = (str) => str.replace(/\s/g, '').toLowerCase();

         const matchByExactName = this.booking_details_fetched.data.filter(
            o => normalizeSearchableNameString(o.parsed.full_name) == normalizeSearchableNameString(guestName)
         );

         if (matchByExactName.length < 1) {
            // Stop if guest name input is a missmatch from cached list.
            let errorMsg = this.$t('checkin_by_phone.name_does_not_match');
            this.errorDisplay2(errorMsg);
            return;
         }

         if (matchByExactName.length > 1) {
            // Stop if guest name input has multiple match found.
            let errorMsg = this.$t('checkin_by_phone.error_multiple_reservation');
            this.errorDisplay2(errorMsg);
            return;
         }

         if (matchByExactName[0].booking_status_id == booking_status.checked_in) {
            // Stop if guest has already checked in (status 2 = checked in)
            let errorMsg = this.$t('checkin_by_phone.info_already_checkedin');
            this.errorDisplay2(errorMsg);
            return;
         }

         if (checkedOutStatusIds.includes(matchByExactName[0].booking_status_id)) {
            // Stop if guest has already checked out (see checkedOutStatusIds above)
            let errorMsg = this.$t('checkin_by_phone.error_already_checkedout');
            this.errorDisplay2(errorMsg);
            return;
         }

         const bookingDetails = { data: matchByExactName, total: 1 };
         this.finalizeSelectedBooking(bookingDetails);
      },
      backToList() {
        this.guest_name = '';
        this.selected_guest_booking = null;
        this.error_msg = '';
      },
      resetState() {
         this.info_msg = '';
         this.error_msg = '';
         this.guest_name = '';
         // When going back from name input, we want to show the list again.
         // The parent component should handle setting the action.
         // If the current action is search_name, we don't clear the fetched bookings.
         if (this.action !== 'search_name' && this.action !== 'show_list_names') {
               this.booking_details_fetched = [];
               this.selected_guest_booking = null;
         }
      },
      errorDisplay2(errorMsg) {
         this.error_msg = errorMsg;
         setTimeout(() => {
            this.error_msg = '';
         }, 4000);
      },
      finalizeSelectedBooking(bookingDetails) {
         const selectedBooking = bookingDetails.data[0];
         const normalizeSearchableNameString = (str) => str.replace(/\s/g, '').toLowerCase();
         
         // Validate that the finalized booking matches the originally selected guest
         if (normalizeSearchableNameString(selectedBooking.parsed.full_name) !== normalizeSearchableNameString(this.selected_guest_booking.parsed.full_name)) {
            let errorMsg = this.$t('checkin_by_phone.name_does_not_match');
            this.errorDisplay2(errorMsg);
            return;
         }
         
         if (selectedBooking.booking_status_id === booking_status.pre_check_in && this.cameFromPciHub) {
            // Guest already completed pre-checkin (by phone or by name) - don't let the
            // PCI hub flow re-enter the confirm/finish-precheckin screen and re-trigger it.
            this.errorDisplay2(this.$t('checkin_by_phone.info_pci_already_completed'));
            return;
         }

         if (selectedBooking.booking_status_id === booking_status.pre_check_in) {
            this.$emit('update-booking', bookingDetails);
            this.$emit('update-next', 'checkin-form');
         } else {
            if (this.localized_filters.length > 0) {
               this.$emit('update-booking', bookingDetails);
               this.$emit('update-next', 'localized-filters');
               this.$emit('update-action', '');
            } else {
               this.$emit('update-booking', bookingDetails);
               this.$emit('update-next', 'checkin-form');
               this.$emit('update-action', '');
            }
         }
      },
   }
}
</script>

<style scoped>

.checkin-by-phone p {
    font-size: 1rem !important;
    padding-top: 24px !important;
    margin-bottom: 12px;
}

.btn-form-control {
    min-width: 156px;
    border-radius: 20px;
}

.btn-custom-primary {
    color: white;
    border: 1px solid #1aa8de !important;
    background-color: #1aa8de !important;

    &:not([disabled]):hover {
        border: 1px solid #0b89bb !important;
        background-color: #0b89bb !important;
    }
}

.search_field {
    text-align: center;
}

/* Search Suggestion Box */
.search-suggestion-box {
    position: relative;

    .suggestion-list {
        display: none;
        position: absolute;
        top: 71px;
        left: 0px;
        background-color: white;
        width: 100%;
        border: 1px solid #ced4da;
        padding-left: 0px;

        li.suggestion {
            display: block;
            width: 100%;
            padding: 5px;
            cursor: pointer;
            opacity: 0.8;

            &:hover {
                background-color: whitesmoke;
                opacity: 1;
            }
        }
    }
}

.list-group-item.active {
    background-color: #e3f2fd;
    color: #1976d2;
}

.guest-list-group {
    padding: 0;
}
.guest-list-item {
    list-style: none;
    margin-bottom: 12px;
    border: none;
    background: transparent;
    padding: 0;
}
.guest-list-input {
    width: 100%;
    background: #fff;
    border: 1px solid #ced4da;
    border-radius: 6px;
    padding: 6px 12px;
    font-size: 1rem;
    color: #333;
    transition: box-shadow 0.2s;
}
.guest-list-item.active .guest-list-input {
    background-color: #e3f2fd;
    color: #1976d2;
    box-shadow: 0 0 0 2px #90caf9;
}

/* .btn-custom-primary {
    color: white;
    border: 1px solid #1aa8de !important;
    background-color: #1aa8de !important;
    &:not([disabled]):hover {
        border: 1px solid #0b89bb !important;
        background-color: #0b89bb !important;
    }
} */
</style>